import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getPrimaryBaseUrl } from '../../config/apiConfig';
import { motion } from 'framer-motion';
import { Shield, CreditCard, Lock, Server, CheckCircle } from 'lucide-react';

interface TrustBarProps {
  variant?: 'inline' | 'section';
  showCounter?: boolean;
  counterValue?: string;
}

const TRUST_ITEM_KEYS = ['rgpd', 'hostedInFrance', 'stripePayment', 'sslSecure'] as const;
const TRUST_ITEM_ICONS = {
  rgpd: Shield,
  hostedInFrance: Server,
  stripePayment: CreditCard,
  sslSecure: Lock,
} as const;

const FALLBACK_PRO_AVATARS = [
  'https://images.unsplash.com/photo-1494790108377-be9c29b29330?auto=format&fit=crop&q=80&w=96&h=96',
  'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?auto=format&fit=crop&q=80&w=96&h=96',
  'https://images.unsplash.com/photo-1534528741775-53994a69daeb?auto=format&fit=crop&q=80&w=96&h=96',
];

export default function TrustBar({
  variant = 'inline',
  showCounter = true,
  counterValue = '500+',
}: TrustBarProps) {
  const { t } = useTranslation('marketing');
  const [proAvatars, setProAvatars] = useState<string[]>([]);

  useEffect(() => {
    const apiBase = getPrimaryBaseUrl()
    if (!apiBase) return;
    fetch(`${apiBase}/get-pros?limit=8`)
      .then((res) => res.json())
      .then((data) => {
        const pros = data?.pros ?? [];
        const urls = pros
          .filter((p: { profile_photo_url?: string }) => p.profile_photo_url)
          .slice(0, 3)
          .map((p: { profile_photo_url: string }) => p.profile_photo_url);
        setProAvatars(urls);
      })
      .catch(() => {});
  }, []);

  if (variant === 'inline') {
    const avatarUrls = proAvatars.length > 0 ? proAvatars : FALLBACK_PRO_AVATARS;
    return (
      <div
        className="lp-trustbar-inline"
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '24px',
          flexWrap: 'wrap',
          fontSize: '14px',
          color: 'var(--mk-text-secondary)',
          fontFamily: 'var(--mk-font-body)',
          fontWeight: 500,
          marginTop: '8px'
        }}
      >
        {showCounter && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <div style={{ display: 'flex', marginLeft: '4px' }}>
              {avatarUrls.map((url, i) => (
                <div
                  key={url}
                  style={{
                    width: '32px',
                    height: '32px',
                    borderRadius: '50%',
                    border: '2px solid #fff',
                    marginLeft: i === 0 ? 0 : '-10px',
                    overflow: 'hidden',
                    background: '#e2e8f0',
                    flexShrink: 0,
                  }}
                >
                  <img
                    src={url}
                    alt=""
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                    }}
                  />
                </div>
              ))}
              <div
                style={{
                  width: '32px',
                  height: '32px',
                  borderRadius: '50%',
                  border: '2px solid #fff',
                  marginLeft: '-10px',
                  background: '#A5765C',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '10px',
                  color: '#fff',
                  fontWeight: 800,
                }}
              >
                +
              </div>
            </div>
            <span style={{ marginLeft: '4px' }}>
              <b>{counterValue}</b> {t('trustBar.professionalsRegistered')}
            </span>
          </div>
        )}
        
        <div className="lp-trustbar-divider" style={{ width: '1px', height: '20px', background: 'rgba(0,0,0,0.06)' }} />
        
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', opacity: 0.7 }}>
          <CheckCircle size={16} color="var(--primary-color)" /> {t('trustBar.items.hostedInFrance')}
        </span>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5 }}
      style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        gap: '32px',
        flexWrap: 'wrap',
        padding: '24px 0',
      }}
    >
      {TRUST_ITEM_KEYS.map((key) => {
        const Icon = TRUST_ITEM_ICONS[key];
        return (
          <div
            key={key}
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '8px',
            }}
          >
            <div
              style={{
                width: '48px',
                height: '48px',
                borderRadius: 'var(--mk-radius-sm)',
                background: 'var(--mk-bg-accent)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Icon size={22} color="var(--mk-brown)" strokeWidth={1.5} />
            </div>
            <span
              style={{
                fontSize: '13px',
                fontWeight: 500,
                color: 'var(--mk-text-secondary)',
                fontFamily: 'var(--mk-font-body)',
              }}
            >
              {t(`trustBar.items.${key}`)}
            </span>
          </div>
        );
      })}
    </motion.div>
  );
}
